import {
  HabitSchedule,
  ScheduleCompletionActions,
  ScheduleEntryState,
  ScheduleTargetEntry,
} from '@griply/types';
import {
  formatDateKey,
  getActualScheduleStartDate,
  getScheduleFrequencyDayTarget,
  isBetween,
} from '@griply/utils';
import { addWeeks, eachDayOfInterval, startOfDay } from 'date-fns';
import { useMemo } from 'react';

import { Options } from './use-timeframe-completions';
import { useTimeframeOccurrences } from './use-timeframe-occurrences';

export const useTimeframeEntries = (
  schedules: HabitSchedule[],
  { startDate, endDate }: Options,
): {
  dateEntries: ScheduleTargetEntry[];
  timeframeDateEntries: ScheduleTargetEntry[];
} => {
  // get a bigger range for extra completions to display in the month-view
  const { rangeStartDate, rangeEndDate } = useMemo(
    () => ({
      rangeStartDate: addWeeks(startDate, -5),
      rangeEndDate: addWeeks(endDate, 5),
    }),
    [endDate, startDate],
  );
  const { occurrences } = useTimeframeOccurrences(schedules, {
    startDate: rangeStartDate,
    endDate: rangeEndDate,
  });

  return useMemo(() => {
    const daysBetween = eachDayOfInterval({
      start: rangeStartDate,
      end: rangeEndDate,
    });
    const targetEntries = daysBetween.map<ScheduleTargetEntry>((date) => ({
      date,
      value: 0,
      target: 0,
      state: ScheduleEntryState.Complete,
      id: `${date.getTime()}`,
      onDateManual: false,
      showValueAsBadge: false,
    }));

    [...schedules]
      .sort(
        (scheduleA, scheduleB) =>
          scheduleA.startDate.getTime() - scheduleB.startDate.getTime(),
      )
      .forEach((schedule, index, array) => {
        const prevSchedule: HabitSchedule | undefined = array[index - 1];

        const scheduleStart = getActualScheduleStartDate(schedule);
        const scheduleStartDate = new Date(
          Math.min(
            startOfDay(prevSchedule?.endDate ?? new Date(0))?.getTime(),
            scheduleStart.getTime(),
          ),
        );
        const scheduleEndDate = schedule.endDate ?? rangeEndDate;
        const scheduleOccurrences = occurrences.filter((date) =>
          isBetween(date, scheduleStartDate, scheduleEndDate),
        );
        const scheduleOccurrenceDateKeys = scheduleOccurrences.map((date) =>
          formatDateKey(date),
        );
        const scheduleDateKeyEntriesMap = schedule.entries.reduce<
          Record<string, number>
        >((acc, entry, currentIndex) => {
          const key = formatDateKey(entry.date);
          acc[key] = currentIndex;
          return acc;
        }, {});

        targetEntries.forEach((entry) => {
          const inDateRange = isBetween(
            entry.date,
            scheduleStartDate,
            scheduleEndDate,
          );

          // find occurrences or entries that match the entry date
          if (inDateRange) {
            const entryDateKey = formatDateKey(entry.date);
            const entryOccurrence =
              scheduleOccurrenceDateKeys.includes(entryDateKey);
            const scheduleEntryIndex = scheduleDateKeyEntriesMap[entryDateKey];
            const scheduleEntry = schedule.entries[scheduleEntryIndex];

            entry.date = scheduleEntry?.date ?? entry.date;
            entry.id = scheduleEntry?.id ?? entry.id;
            entry.state = scheduleEntry?.state ?? entry.state;
            entry.value = scheduleEntry?.value ?? entry.value;
            entry.hidden = scheduleEntry?.hidden ?? entry.hidden;
            entry.target = schedule.frequency
              ? getScheduleFrequencyDayTarget(schedule)
              : entryOccurrence
                ? schedule.completionTarget.count
                : entry.target;
            entry.onDateManual =
              schedule.action?.type ===
              ScheduleCompletionActions.LogCompletionValue;
            entry.showValueAsBadge = (entry.value ?? 0) > 1;
          }
        });
      });

    return {
      dateEntries: targetEntries.filter(({ date }) =>
        isBetween(date, rangeStartDate, rangeEndDate),
      ),
      timeframeDateEntries: targetEntries.filter(({ date }) =>
        isBetween(date, startDate, endDate),
      ),
    };
  }, [
    endDate,
    occurrences,
    rangeEndDate,
    rangeStartDate,
    schedules,
    startDate,
  ]);
};
